'use client';

import { faArrowDown, faChevronDown } from '@fortawesome/pro-light-svg-icons';
import { faAngleDown, faBarsFilter, faSearch, faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@pickleballinc/ui/components/ui/button';
import {
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerDescription,
	DrawerHeader,
	DrawerPortal,
	DrawerTitle
} from '@pickleballinc/ui/components/ui/drawer';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuPortal,
	DropdownMenuTrigger
} from '@pickleballinc/ui/components/ui/dropdown-menu';
import { Icon } from '@pickleballinc/ui/components/ui/icon';
import { Input } from '@pickleballinc/ui/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@pickleballinc/ui/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@pickleballinc/ui/components/ui/select';
import clsx from 'clsx';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';

import { sortOptions } from '@/components/constants/constants';
import { MobileSkillRange } from '@/components/molecules/MobileSkillRange';
import { SkillRange } from '@/components/molecules/SkillRange';
import { useTeamLeagueInfo } from '@/hooks/custom-hooks/useTeamLeagueInfo';
import { Options } from '@/utils/types';

export interface PlayersPageFilters {
	season: string;
	district: string;
	division: string;
	player_status: string;

	search: string;
	age: string;
	gender: string;
	sortBy: string;
	orderBy: string;
	singles_skill: string;
	doubles_skill: string;

	mobile_filters: string;
}

interface FiltersForPlayersPageProps {
	seasons: Options[];
	divisions: Options[];
	districts: Options[];
	activeSeason?: string;
}

export interface AgeFilterOptions {
	label: string;
	value: number[];
}

export const AgeFilterOptions: AgeFilterOptions[] = [
	{ label: 'All', value: [-1] },
	{ label: '18u', value: [0, 17] },
	{ label: '18-29', value: [18, 29] },
	{ label: '30-39', value: [30, 39] },
	{ label: '40-49', value: [40, 49] },
	{ label: '50-59', value: [50, 59] },
	{ label: '60-69', value: [60, 69] },
	{ label: '70-79', value: [70, 79] },
	{ label: '80+', value: [80, 100] }
] as const;

export const GenderFilterOptions = [
	{ label: 'All', value: 'all' },
	{ label: 'Male', value: 'M' },
	{ label: 'Female', value: 'F' }
] as const;

const getNewSearchParams = (filters: PlayersPageFilters) => {
	const newSearchParams = new URLSearchParams(window.location.search);

	newSearchParams.delete('current_page');

	if (filters.season) {
		newSearchParams.set('season', filters.season);
	} else {
		newSearchParams.delete('season');
	}

	if (filters.district && filters.district !== 'all') {
		newSearchParams.set('district', filters.district);
	} else {
		newSearchParams.delete('district');
	}

	if (filters.division && filters.division !== 'all') {
		newSearchParams.set('division', filters.division);
	} else {
		newSearchParams.delete('division');
	}

	if (filters.player_status && filters.player_status !== 'all') {
		newSearchParams.set('show_free_agents', filters.player_status);
	} else {
		newSearchParams.delete('show_free_agents');
	}

	if (filters.search) {
		newSearchParams.set('search', filters.search);
	} else {
		newSearchParams.delete('search');
	}

	if (filters.age && filters.age !== '-1') {
		newSearchParams.set('age', filters.age);
	} else {
		newSearchParams.delete('age');
	}

	if (filters.gender && filters.gender !== 'all') {
		newSearchParams.set('gender', filters.gender);
	} else {
		newSearchParams.delete('gender');
	}

	if (filters.sortBy) {
		newSearchParams.set('sort_by', filters.sortBy);
	} else {
		newSearchParams.delete('sort_by');
	}

	if (filters.orderBy) {
		newSearchParams.set('order_by', filters.orderBy);
	} else {
		newSearchParams.delete('order_by');
	}

	if (filters.singles_skill) {
		newSearchParams.set('singles_skill', filters.singles_skill);
	} else {
		newSearchParams.delete('singles_skill');
	}

	if (filters.doubles_skill) {
		newSearchParams.set('doubles_skill', filters.doubles_skill);
	} else {
		newSearchParams.delete('doubles_skill');
	}

	return newSearchParams || '';
};

export const FiltersForPlayersPage = ({ seasons, divisions, districts, activeSeason }: FiltersForPlayersPageProps) => {
	const router = useRouter();
	const searchParams = useSearchParams();

	const [filters, setFilters] = useState<PlayersPageFilters>({
		season: searchParams?.get('season') ?? activeSeason ?? seasons[0]?.value ?? '',
		division: searchParams?.get('division') ?? '',
		district: searchParams?.get('district') ?? '',
		player_status: searchParams?.get('show_free_agents') ?? '',
		search: searchParams?.get('search') ?? '',
		age: searchParams?.get('age') ?? '',
		gender: searchParams?.get('gender') ?? '',
		sortBy: searchParams?.get('sort_by') ?? 'first_name',
		orderBy: searchParams?.get('order_by') ?? 'asc',
		singles_skill: searchParams?.get('singles_skill') ?? '',
		doubles_skill: searchParams?.get('doubles_skill') ?? '',

		mobile_filters: ''
	});

	useEffect(() => {
		if (!searchParams.get('season') && activeSeason) {
			const params = new URLSearchParams(searchParams.toString());
			params.set('season', activeSeason);
			router.replace(`?${params.toString()}`);
		}
	}, [searchParams]);

	const seasonsWithAllOption: Options[] = seasons.length >= 1 ? [{ value: 'all', label: 'All Seasons' }, ...seasons] : [];
	const divisionsWithAllOption: Options[] = divisions.length >= 1 ? [{ value: 'all', label: 'All divisions' }, ...divisions] : [];
	const districtsWithAllOption: Options[] = districts.length >= 1 ? [{ value: 'all', label: 'All districts' }, ...districts] : [];

	const playerStatusWithAllPlayersOption: Options[] = [
		{ value: 'all', label: 'All players' },
		{ value: 'false', label: 'On a Roster only' },
		{ value: 'true', label: 'Free Agent only' }
	];

	const age =
		AgeFilterOptions.find((option) => {
			const split = filters.age ? filters.age.split('-').map((s) => Number(s)) : '';
			return split && split[0] === option.value[0] && split[1] === option.value[1];
		}) ?? AgeFilterOptions[0];
	const gender = GenderFilterOptions.find((option) => option.value === filters.gender) ?? GenderFilterOptions[0];
	const singlesSkill = filters.singles_skill ? filters.singles_skill.split('-').map((s) => Number(s)) : [0.0, 8.0];
	const doublesSkill = filters.doubles_skill ? filters.doubles_skill.split('-').map((s) => Number(s)) : [0.0, 8.0];

	const emptyFilters: PlayersPageFilters = {
		season: activeSeason ?? seasons[0]?.value ?? '',
		district: '',
		division: '',
		player_status: '',
		search: '',
		age: '',
		gender: '',
		sortBy: '',
		orderBy: '',
		singles_skill: '',
		doubles_skill: '',
		mobile_filters: ''
	};

	const handleReset = () => {
		setFilters(emptyFilters);
		router.replace(`?${getNewSearchParams(emptyFilters).toString()}`);
	};

	return (
		<div className="flex flex-col gap-4 mt-4 lg:mt-6 lg:mb-2">
			<div className="hidden md:grid gap-6 md:grid-cols-3 w-full">
				<Select
					onValueChange={(value) => setFilters({ ...filters, season: value })}
					value={filters.season || activeSeason || seasons[0]?.value}
				>
					<SelectTrigger
						className="bg-white focus:ring-0 h-10"
						icon={({ className }: { className: string }) => <FontAwesomeIcon icon={faChevronDown} className={className} />}
					>
						<SelectValue placeholder="Select season" />
					</SelectTrigger>
					<SelectContent>
						{seasonsWithAllOption.map((season) => (
							<SelectItem key={season.value} value={season.value}>
								{season.label}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>
			<div className="hidden md:grid w-full gap-6 md:grid-cols-3 lg:grid-cols-3">
				<Select
					onValueChange={(value) => setFilters({ ...filters, district: value })}
					value={filters.district || districtsWithAllOption[0]?.value}
				>
					<SelectTrigger
						id="district-select"
						className={clsx('bg-white focus:ring-0 h-10', {
							hidden: districts.length === 1
						})}
						icon={({ className }: { className: string }) => <FontAwesomeIcon icon={faChevronDown} className={className} />}
					>
						<SelectValue placeholder="Select district" />
					</SelectTrigger>
					<SelectContent>
						{districtsWithAllOption.map((district) => (
							<SelectItem key={district.value} value={district.value}>
								{district.label}
							</SelectItem>
						))}
					</SelectContent>
				</Select>

				<Select
					onValueChange={(value) => setFilters({ ...filters, division: value })}
					value={filters.division || divisionsWithAllOption[0]?.value}
				>
					<SelectTrigger
						id="division-select"
						className={clsx('bg-white focus:ring-0 h-10', {
							hidden: divisions.length === 1
						})}
						icon={({ className }: { className: string }) => <FontAwesomeIcon icon={faChevronDown} className={className} />}
					>
						<SelectValue placeholder="Select division" />
					</SelectTrigger>
					<SelectContent>
						{divisionsWithAllOption.map((division) => (
							<SelectItem key={division.value} value={division.value}>
								{division.label}
							</SelectItem>
						))}
					</SelectContent>
				</Select>

				<Select
					onValueChange={(value) => setFilters({ ...filters, player_status: value })}
					value={filters.player_status || playerStatusWithAllPlayersOption[0]?.value}
				>
					<SelectTrigger
						id="player-status-select"
						className="bg-white focus:ring-0 h-10"
						icon={({ className }: { className: string }) => <FontAwesomeIcon icon={faChevronDown} className={className} />}
					>
						<SelectValue placeholder="All Players" />
					</SelectTrigger>
					<SelectContent>
						{playerStatusWithAllPlayersOption.map((playerStatusOption) => (
							<SelectItem key={playerStatusOption.value} value={playerStatusOption.value}>
								{playerStatusOption.label}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>

			<div className="flex flex-col w-full pb-6 border-b">
				<div className="flex w-full max-w-screen-xl items-start justify-between gap-2 px-0 md:flex-col xl:grid xl:grid-cols-3 xl:gap-6">
					<div className="flex gap-2 w-full">
						<div className="relative w-full md:w-1/2 xl:w-full" data-testid="players-search">
							<Input
								type="text"
								placeholder="Search"
								value={filters.search}
								onChange={(e) => setFilters({ ...filters, search: e.target.value })}
								onKeyUp={(event: React.KeyboardEvent<HTMLInputElement>) => {
									if (event.key === 'Enter') {
										router.replace(`?${getNewSearchParams(filters)?.toString()}`);
										event.currentTarget.blur();
									}
								}}
								className="h-10 w-full border border-gray-300 pl-8 bg-white focus-visible:ring-0"
								maxLength={100}
								data-testid="players-search-field"
							/>
							<FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
						</div>
						<Button
							className="flex w-fit md:hidden"
							size="lg"
							onClick={() => {
								router.replace(`?${getNewSearchParams(filters)?.toString()}`);
							}}
						>
							Search
						</Button>
					</div>

					<div className="hidden md:flex lg:hidden w-full flex-wrap items-center gap-2 pt-2">
						<div>
							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<Button
										variant="outline"
										className={'!rounded-lg  !font-normal  transition-all text-gray-500 !w-fit !px-2'}
										size="lg"
										data-testid="players-age-button"
									>
										Age
										<FontAwesomeIcon icon={faAngleDown} />
									</Button>
								</DropdownMenuTrigger>
								<DropdownMenuPortal>
									<DropdownMenuContent className="animate-fade-in-down w-fit min-w-[200px] rounded-lg border border-gray-300 bg-white drop-shadow-md">
										{AgeFilterOptions.map((option) => (
											<DropdownMenuItem
												key={option.label}
												onClick={() => setFilters({ ...filters, age: option.value.join('-') })}
												data-testid={`players-age-option-${option.label}`}
											>
												{option.label}
											</DropdownMenuItem>
										))}
									</DropdownMenuContent>
								</DropdownMenuPortal>
							</DropdownMenu>
						</div>

						<div>
							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<Button
										variant="outline"
										className={'!rounded-lg !font-normal !text-gray-500 transition-all !px-2'}
										size="lg"
										data-testid="players-gender-button"
									>
										Gender
										<FontAwesomeIcon icon={faAngleDown} />
									</Button>
								</DropdownMenuTrigger>
								<DropdownMenuPortal>
									<DropdownMenuContent className="animate-fade-in-down w-fit min-w-[200px] rounded-lg border border-gray-300 bg-white drop-shadow-md">
										{GenderFilterOptions.map((option) => (
											<DropdownMenuItem
												key={option.value}
												onClick={() => setFilters({ ...filters, gender: option.value })}
												data-testid={`players-gender-option-${option.label}`}
											>
												{option.label}
											</DropdownMenuItem>
										))}
									</DropdownMenuContent>
								</DropdownMenuPortal>
							</DropdownMenu>
						</div>

						<SkillRange
							setSkill={(values) => {
								if (values[0] === 0 && values[1] === 8) {
									setFilters({ ...filters, doubles_skill: '' });
								} else {
									setFilters({ ...filters, doubles_skill: values.join('-') });
								}
							}}
							skill={doublesSkill}
							filterName="Double Rating"
						/>
						<SkillRange
							setSkill={(values) => {
								if (values[0] === 0 && values[1] === 8) {
									setFilters({ ...filters, singles_skill: '' });
								} else {
									setFilters({ ...filters, singles_skill: values.join('-') });
								}
							}}
							skill={singlesSkill}
							filterName="Single Rating"
						/>
						<ApplyResetButtons onApply={() => router.replace(`?${getNewSearchParams(filters)?.toString()}`)} onReset={handleReset} />
					</div>

					{/* Desktop-only filters group (middle column) */}
					<div className="hidden lg:flex items-center lg:flex-nowrap gap-2 w-full">
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button
									variant="outline"
									className="!rounded-lg !font-normal transition-all gap-1 flex text-gray-500 !px-2"
									size="lg"
									data-testid="players-age-button-desktop"
								>
									Age
									<FontAwesomeIcon icon={faAngleDown} />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuPortal>
								<DropdownMenuContent className="animate-fade-in-down w-fit min-w-[200px] rounded-lg border border-gray-300 bg-white drop-shadow-md">
									{AgeFilterOptions.map((option) => (
										<DropdownMenuItem key={option.label} onClick={() => setFilters({ ...filters, age: option.value.join('-') })}>
											{option.label}
										</DropdownMenuItem>
									))}
								</DropdownMenuContent>
							</DropdownMenuPortal>
						</DropdownMenu>

						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button
									variant="outline"
									className="!rounded-lg !font-normal !text-gray-500 transition-all flex gap-1 !px-2"
									size="lg"
									data-testid="players-gender-button-desktop"
								>
									Gender
									<FontAwesomeIcon icon={faAngleDown} />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuPortal>
								<DropdownMenuContent className="animate-fade-in-down w-fit min-w-[200px] rounded-lg border border-gray-300 bg-white drop-shadow-md">
									{GenderFilterOptions.map((option) => (
										<DropdownMenuItem key={option.value} onClick={() => setFilters({ ...filters, gender: option.value })}>
											{option.label}
										</DropdownMenuItem>
									))}
								</DropdownMenuContent>
							</DropdownMenuPortal>
						</DropdownMenu>

						<SkillRange
							setSkill={(values) =>
								setFilters({ ...filters, doubles_skill: values[0] === 0 && values[1] === 8 ? '' : values.join('-') })
							}
							skill={doublesSkill}
							filterName="Double Rating"
						/>
						<SkillRange
							setSkill={(values) =>
								setFilters({ ...filters, singles_skill: values[0] === 0 && values[1] === 8 ? '' : values.join('-') })
							}
							skill={singlesSkill}
							filterName="Single Rating"
						/>

						<ApplyResetButtons onApply={() => router.replace(`?${getNewSearchParams(filters)?.toString()}`)} onReset={handleReset} />
					</div>

					{/* Desktop-only buttons group (right column) */}
					<ApplyResetButtons
						onApply={() => router.replace(`?${getNewSearchParams(filters)?.toString()}`)}
						onReset={handleReset}
						isDesktop
					/>
				</div>

				{/* Mobile Filters Button */}
				<Button
					variant="outline"
					className="animate-fade-in-down flex w-full items-center justify-center mt-2 md:hidden"
					size="lg"
					onClick={() => setFilters({ ...filters, mobile_filters: 'true' })}
				>
					<FontAwesomeIcon icon={faBarsFilter} className="mr-2" />
					Filters
				</Button>

				<MobileFiltersForPlayersPage
					filters={filters}
					setFilters={setFilters}
					gender={gender}
					handleReset={handleReset}
					activeSeason={activeSeason}
					seasons={seasons}
					divisions={divisions}
					districts={districts}
					seasonsWithAllOption={seasonsWithAllOption}
					divisionsWithAllOption={divisionsWithAllOption}
					districtsWithAllOption={districtsWithAllOption}
					playerStatusWithAllPlayersOption={playerStatusWithAllPlayersOption}
					age={age}
				/>
			</div>

			<div className="container mx-auto hidden max-w-screen-xl px-6 pt-2 lg:flex">
				{!!age && age.value.length > 1 && (
					<div className="mr-1 inline-flex items-center rounded-md bg-gray-200 px-4 py-1 text-sm text-black">
						{AgeFilterOptions.find((option) => age.value === option.value)?.label} years
						<div
							className="ml-2 cursor-pointer text-gray-400 hover:text-black"
							onClick={() => setFilters({ ...filters, age: '' })}
							role="presentation"
						>
							<FontAwesomeIcon icon={faTimes} />
						</div>
					</div>
				)}

				{!!gender && gender.value !== 'all' && (
					<div className="mr-1 inline-flex items-center rounded-md bg-gray-200 px-4 py-1 text-sm text-black">
						{`${gender.value === 'M' ? 'Male' : 'Female'}`}
						<div
							className="ml-2 cursor-pointer text-gray-400 hover:text-black"
							onClick={() => setFilters({ ...filters, gender: '' })}
							role="presentation"
						>
							<FontAwesomeIcon icon={faTimes} />
						</div>
					</div>
				)}

				{singlesSkill.length > 0 && ((singlesSkill[0] && singlesSkill[0] > 0) || (singlesSkill[1] && singlesSkill[1] < 8)) ? (
					<div className="mr-1 inline-flex items-center rounded-md bg-gray-200 px-4 py-1 text-sm text-black">
						{`Single Rating: ${singlesSkill[0]?.toFixed(2)} - ${singlesSkill[1]?.toFixed(2)}`}
						<div
							className="ml-2 cursor-pointer text-gray-400 hover:text-black"
							onClick={() => setFilters({ ...filters, singles_skill: '' })}
							role="presentation"
						>
							<FontAwesomeIcon icon={faTimes} />
						</div>
					</div>
				) : null}

				{doublesSkill.length > 0 && ((doublesSkill[0] && doublesSkill[0] > 0) || (doublesSkill[1] && doublesSkill[1] < 8)) ? (
					<div className="mr-1 inline-flex items-center rounded-md bg-gray-200 px-4 py-1 text-sm text-black">
						{`Double Rating: ${doublesSkill[0]?.toFixed(2)} - ${doublesSkill[1]?.toFixed(2)}`}
						<div
							className="ml-2 cursor-pointer text-gray-400 hover:text-black"
							onClick={() => setFilters({ ...filters, doubles_skill: '' })}
							role="presentation"
						>
							<FontAwesomeIcon icon={faTimes} />
						</div>
					</div>
				) : null}
			</div>
		</div>
	);
};

interface MobileFiltersForPlayersPageProps {
	filters: PlayersPageFilters;
	gender: {
		label: string;
		value: string;
	};
	handleReset: () => void;
	setFilters: (filters: PlayersPageFilters) => void;
	seasons: Options[];
	divisions: Options[];
	districts: Options[];
	activeSeason?: string;
	seasonsWithAllOption: Options[];
	divisionsWithAllOption: Options[];
	districtsWithAllOption: Options[];
	playerStatusWithAllPlayersOption: Options[];
	age?: AgeFilterOptions;
}

export const MobileFiltersForPlayersPage = ({
	filters,
	gender,
	handleReset,
	setFilters,
	seasons,
	divisions,
	districts,
	activeSeason,
	seasonsWithAllOption,
	divisionsWithAllOption,
	districtsWithAllOption,
	playerStatusWithAllPlayersOption
}: MobileFiltersForPlayersPageProps) => {
	const router = useRouter();
	const teamLeagueInfo = useTeamLeagueInfo();

	const isSliderOpen = filters.mobile_filters === 'true';

	const singlesSkill = filters.singles_skill ? filters.singles_skill.split('-').map((s) => Number(s)) : [0.0, 8.0];
	const doublesSkills = filters.doubles_skill ? filters.doubles_skill.split('-').map((s) => Number(s)) : [0.0, 8.0];

	const updateSkillFilter = (values: number[], skillKey: string) => {
		setFilters({
			...filters,
			[skillKey]: values[0] === 0 && values[1] === 8 ? '' : values.join('-')
		});
	};

	const modifiedSortOptions: Options[] = sortOptions
		.filter((option) => option.value !== 'standing')
		.map((option) => ({
			...option,
			label: option.label.startsWith('TB ') ? option.label.replace('TB', teamLeagueInfo.tieBreakerMatchAbbreviation ?? 'TB') : option.label
		}));

	return (
		<Drawer open={isSliderOpen} onClose={() => setFilters({ ...filters, mobile_filters: '' })}>
			<DrawerPortal>
				<DrawerContent className="flex w-full flex-col bg-white px-4 pb-4 pt-1 md:hidden h-[90%]">
					<DrawerDescription className="sr-only">Filters for teams page</DrawerDescription>
					<div className="overflow-y-auto">
						<DrawerHeader className="flex justify-between">
							<DrawerTitle>Filters</DrawerTitle>
							<DrawerClose>
								<Icon glyph="close" />
							</DrawerClose>
						</DrawerHeader>
						<div className="mt-6 w-full">
							<Select
								onValueChange={(value) => setFilters({ ...filters, season: value })}
								value={filters.season || activeSeason || seasons[0]?.value}
							>
								<SelectTrigger
									className="bg-white focus:ring-0 h-10"
									icon={({ className }: { className: string }) => <FontAwesomeIcon icon={faChevronDown} className={className} />}
								>
									<SelectValue placeholder="Select season" />
								</SelectTrigger>
								<SelectContent>
									{seasonsWithAllOption.map((season) => (
										<SelectItem key={season.value} value={season.value}>
											{season.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						{districts.length > 1 && (
							<div className="mt-6 w-full">
								<Select
									onValueChange={(value) => setFilters({ ...filters, district: value })}
									value={filters.district || districtsWithAllOption[0]?.value}
								>
									<SelectTrigger
										id="district-select"
										className="bg-white focus:ring-0 h-10"
										icon={({ className }: { className: string }) => (
											<FontAwesomeIcon icon={faChevronDown} className={className} />
										)}
									>
										<SelectValue placeholder="Select district" />
									</SelectTrigger>
									<SelectContent className="z-[2000]">
										{districtsWithAllOption.map((district) => (
											<SelectItem key={district.value} value={district.value}>
												{district.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
						)}

						{divisions.length > 1 && (
							<div className="mt-6  w-full">
								<Select
									onValueChange={(value) => setFilters({ ...filters, division: value })}
									value={filters.division || divisionsWithAllOption[0]?.value}
								>
									<SelectTrigger
										id="division-select"
										className="bg-white focus:ring-0 h-10"
										icon={({ className }: { className: string }) => (
											<FontAwesomeIcon icon={faChevronDown} className={className} />
										)}
									>
										<SelectValue placeholder="Select division" />
									</SelectTrigger>
									<SelectContent className="z-[2000]">
										{divisionsWithAllOption.map((division) => (
											<SelectItem key={division.value} value={division.value}>
												{division.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
						)}

						<div className="mt-6 w-full">
							<Select
								onValueChange={(value) => setFilters({ ...filters, player_status: value })}
								value={filters.player_status || playerStatusWithAllPlayersOption[0]?.value}
							>
								<SelectTrigger
									id="player-status-select"
									className="bg-white focus:ring-0 h-10"
									icon={({ className }: { className: string }) => <FontAwesomeIcon icon={faChevronDown} className={className} />}
								>
									<SelectValue placeholder="All Players" />
								</SelectTrigger>
								<SelectContent className="z-[2000]">
									{playerStatusWithAllPlayersOption.map((playerStatusOption) => (
										<SelectItem key={playerStatusOption.value} value={playerStatusOption.value}>
											{playerStatusOption.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						<div className={clsx('mt-6 border-b border-gray-200', isSliderOpen ? 'flex flex-col' : 'hidden')}>
							<div className="mb-1 text-sm font-semibold text-gray-900">Age</div>
							<Select
								onValueChange={(value) => setFilters({ ...filters, age: value })}
								value={filters.age || AgeFilterOptions[0]?.value.join('-')}
							>
								<SelectTrigger
									id="age-select"
									className="bg-white focus:ring-0 h-10"
									icon={({ className }: { className: string }) => <FontAwesomeIcon icon={faChevronDown} className={className} />}
								>
									<SelectValue placeholder="Age" />
								</SelectTrigger>
								<SelectContent className="z-[2000]">
									{AgeFilterOptions.map((ageOption) => (
										<SelectItem key={ageOption.value.join('-')} value={ageOption.value.join('-')}>
											{ageOption.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						<div className="mt-6 flex flex-col border-b border-gray-200">
							<div className="mb-1 text-sm font-semibold text-gray-900">Gender</div>
							<div className="mb-4 flex items-center gap-x-4">
								<Button
									variant={gender.value === 'M' ? `default` : `secondary`}
									className={`!flex !flex-1 !justify-center !text-sm ${gender.value === 'M' && '!bg-gray-900'}`}
									onClick={() => setFilters({ ...filters, gender: gender.value === 'M' ? '' : 'M' })}
									data-testid="players-gender-option-Male"
								>
									Male
								</Button>
								<Button
									variant={gender.value === 'F' ? `default` : `secondary`}
									className={`!flex !flex-1 !justify-center !text-sm ${gender.value === 'F' && '!bg-gray-900'}`}
									onClick={() => setFilters({ ...filters, gender: gender.value === 'F' ? '' : 'F' })}
									data-testid="players-gender-option-Female"
								>
									Female
								</Button>
							</div>
						</div>

						<div className="mt-6 flex flex-col gap-1 w-full mb-4">
							<p className="text-sm font-medium leading-5 text-gray-700">Sort players by</p>
							<Select
								onValueChange={(value) => setFilters({ ...filters, sortBy: value.toString() })}
								value={filters.sortBy || modifiedSortOptions[0]?.value}
							>
								<SelectTrigger
									className="bg-white focus:ring-0"
									icon={({ className }: { className: string }) => <FontAwesomeIcon icon={faChevronDown} className={className} />}
								>
									<SelectValue placeholder="Rank" />
								</SelectTrigger>
								<SelectContent>
									{modifiedSortOptions.map((option) => (
										<SelectItem key={option.value} value={option.value}>
											{option.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						<RadioGroup
							className="flex flex-row gap-4"
							value={filters.orderBy}
							onValueChange={(value) => setFilters({ ...filters, orderBy: value })}
							name="filters"
						>
							<div className="flex items-center">
								<RadioGroupItem
									id="desc"
									value="desc"
									className="w-4 h-4 cursor-pointer text-orange-500 border-gray-300 focus:ring-orange-500 accent-orange-500 shadow-none"
								/>

								<label
									className={clsx(`text-md ml-2`, {
										'text-primary-500': filters.orderBy === 'desc'
									})}
									htmlFor="desc"
								>
									Descending
								</label>
								<FontAwesomeIcon
									icon={faArrowDown}
									className={clsx(`size-3 ml-2`, {
										'text-primary-500': filters.orderBy === 'desc'
									})}
								/>
							</div>

							<div className="flex items-center">
								<RadioGroupItem
									id="asc"
									value="asc"
									className="w-4 h-4 cursor-pointer text-orange-500 border-gray-300 focus:ring-orange-500 accent-orange-500 shadow-none"
								/>
								<label
									className={clsx(`text-md ml-2`, {
										'text-primary-500': filters.orderBy !== 'desc'
									})}
									htmlFor="asc"
								>
									Ascending
								</label>
								<FontAwesomeIcon
									icon={faArrowDown}
									className={clsx(`size-3 ml-2 rotate-180`, {
										'text-primary-500': filters.orderBy !== 'desc'
									})}
								/>
							</div>
						</RadioGroup>

						<div className="mt-6 flex flex-col border-b border-gray-200" data-testid="players-doubles-skill-range">
							<div className="mb-8 text-sm font-semibold text-gray-900">Double Rating</div>
							<MobileSkillRange setSkillRange={(values) => updateSkillFilter(values, 'doubles_skill')} rangeValue={doublesSkills} />
						</div>

						<div className="mt-4 flex flex-col border-b border-gray-200" data-testid="players-singles-skill-range">
							<div className="mb-8 text-sm font-semibold text-gray-900">Single Rating</div>
							<MobileSkillRange setSkillRange={(values) => updateSkillFilter(values, 'singles_skill')} rangeValue={singlesSkill} />
						</div>

						<div className="mt-4 flex items-center justify-end">
							<Button
								variant="secondary"
								className="mr-2 flex text-sm"
								onClick={handleReset}
								data-testid="players-button-reset"
								size="lg"
							>
								Reset
							</Button>
							<Button
								className={'!flex w-fit !justify-center !text-sm bg-orange-500 hover:bg-orange-700 text-white'}
								size="lg"
								onClick={() => {
									setFilters({ ...filters, mobile_filters: '' });
									router.replace(`?${getNewSearchParams(filters)?.toString()}`);
								}}
							>
								Apply
							</Button>
						</div>
					</div>
				</DrawerContent>
			</DrawerPortal>
		</Drawer>
	);
};

interface ApplyResetButtonsProps {
	onApply: () => void;
	onReset: () => void;
	isMobile?: boolean;
	isDesktop?: boolean;
	className?: string;
}

export const ApplyResetButtons = ({ onApply, onReset, isMobile = false, isDesktop = false, className = '' }: ApplyResetButtonsProps) => {
	if (isMobile) {
		// Mobile version
		return (
			<div className={`mt-4 flex items-center justify-end ${className}`}>
				<Button variant="outline" className="mr-2 flex text-sm" onClick={onReset} data-testid="players-button-reset" size="lg">
					Reset
				</Button>
				<Button className="!flex w-fit !justify-center !text-sm" onClick={onApply} size="lg">
					Apply
				</Button>
			</div>
		);
	}

	if (isDesktop) {
		// Desktop version
		return (
			<div className={`hidden xl:flex flex-row gap-4 justify-end ${className}`}>
				<Button onClick={onApply} size="lg">
					<span className="lg:hidden">Search</span>
					<span className="hidden lg:inline">Apply</span>
				</Button>

				<Button onClick={onReset} className="px-[18px] py-2.5" size="lg" variant="outline">
					Reset
				</Button>
			</div>
		);
	}

	// Default/tablet version
	return (
		<div className={`flex gap-4 ml-auto xl:hidden ${className}`}>
			<Button onClick={onApply} size="lg">
				Apply
			</Button>

			<Button onClick={onReset} className="px-[18px] py-2.5" size="lg" variant="outline">
				Reset
			</Button>
		</div>
	);
};
